import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ConfirmModal from 'shared/components/ConfirmModal';
import MainSpinner from 'shared/components/MainSpinner';

import useApi from 'hooks/useApi';
import {
  deleteContactTemporary,
  loadContacts,
  refreshContacts,
  removeContact,
  selectRemoveContact,
  setOpenDelete,
} from 'redux/actions/contactActions';
import { showAlert } from 'redux/actions/alertActions';

import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS } from 'shared/utils';

import ContactsHeader from './ContactsHeader';
import ContactsTable from './ContactsTable';
import ContactModalForm from './ContactModalForm';
import { ContactsWrapper } from '../styles';

const MANUALLY_SYNC = 2;

const Contacts = ({
  clientId,
  updating,
  newTypeOrganization,
  newClientName,
  newContactPhone,
  languageField,
  setLanguageField,
}) => {
  const dispatch = useDispatch();
  const { cusNavId } = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const theme = useSelector((state) => state.theme);
  const { syncContacts } = theme;
  const {
    refresh,
    searchValue,
    sortConfig,
    selectedRemoveContact,
    addContacts,
    isOpenDelete,
    isManuallySync,
    openContactModalForm,
  } = useSelector((state) => state.contact);

  const { customAreaApi } = useApi();

  const [{ loading }, getContacts] = customAreaApi(
    {
      url: `${window.API_V1}/cli/contacts/${cusNavId}/search/all`,
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: loadingDelete }, deleteContact] = customAreaApi(
    {
      method: 'DELETE',
    },
    { manual: true }
  );

  useEffect(() => {
    if (!updating) {
      dispatch(
        loadContacts(
          addContacts.map((item) => {
            return { ...item, popoverOpen: false };
          })
        )
      );
    }
  }, [!updating, addContacts]);

  const fetchContacts = async (searchParam = undefined) => {
    if (!updating) {
      if (addContacts.length > 0) {
        dispatch(
          loadContacts(
            addContacts.map((item) => {
              return { ...item, popoverOpen: false };
            })
          )
        );
      }
    } else if (clientId) {
      try {
        const {
          data: { data, errors },
        } = await getContacts(searchParam ? { data: searchParam } : undefined);
        if (errors.length) {
          return;
        }
        const currentContacts =
          data.map((item) => {
            return { ...item, popoverOpen: false };
          }) ?? [];

        if (currentContacts.length > 1) {
          dispatch(loadContacts(currentContacts));
          return;
        }
        if (currentContacts[0].mail) {
          dispatch(loadContacts(currentContacts));
          return;
        }
        dispatch(loadContacts([]));
      } catch (ex) {
        dispatch(loadContacts([]));
      }
    }
  };

  const searchContacts = () => {
    const sortOption = sortConfig.find((currentSortOption) => currentSortOption.asc !== 'none');
    const searchParam = sortOption
      ? {
          filters: [
            {
              model: 'ClientAccess',
              field: 'ClientId',
              filter: [clientId],
            },
          ],
          orders: [...sortConfig],
          reduce: {
            limit: 0,
            skip: 0,
          },
        }
      : {
          filters: [
            {
              model: 'ClientAccess',
              field: 'ClientId',
              filter: [clientId],
            },
          ],
          orders: [{ model: 'Contact', field: 'Mail', asc: true }],
          reduce: {
            limit: 0,
            skip: 0,
          },
        };
    if (searchValue && searchValue.length >= 2) {
      searchParam.filterText = {
        regexFilter: searchValue,
      };
    }
    fetchContacts(searchParam);
  };

  useEffect(() => {
    if (refresh) {
      dispatch(refreshContacts(false));
      searchContacts();
    }
  }, [refresh]);

  useEffect(() => {
    if (searchValue != null && searchValue.length >= 2 && !loading) {
      searchContacts();
    }
    if (searchValue != null && searchValue.length < 1 && !loading) {
      searchContacts();
    }
  }, [searchValue]);

  useEffect(() => {
    if (clientId.length) {
      searchContacts();
    }
    return () => {
      dispatch(loadContacts([]));
    };
  }, [sortConfig, clientId]);

  const onCancelRemove = () => {
    dispatch(selectRemoveContact(undefined));
    dispatch(setOpenDelete(false));
  };

  const onOkRemove = async () => {
    if (!selectedRemoveContact.isBD && !clientId) {
      dispatch(deleteContactTemporary({ id: selectedRemoveContact.clientId }));
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_SUCCESS,
          alertMessage: t('client_contact_delete_success').replace(
            '{contactName}',
            `${selectedRemoveContact?.name} ${selectedRemoveContact?.surname ?? ''} ${
              selectedRemoveContact?.surnameTwo ?? ''
            }`
          ),
        })
      );
      onCancelRemove();
    } else {
      try {
        if (loadingDelete) {
          return;
        }
        const deleteUrl =
          syncContacts === MANUALLY_SYNC
            ? `${window.API_V1}/cli/contacts/${cusNavId}/${selectedRemoveContact.mail}/access/${clientId}?syncClientlink=${isManuallySync}`
            : `${window.API_V1}/cli/contacts/${cusNavId}/${selectedRemoveContact.mail}/access/${clientId}`;
        const {
          data: { errors },
        } = await deleteContact({
          url: deleteUrl,
        });
        if (errors.length) {
          throw new Error(
            errors.reduce((actual, current) => `${actual}\n${current.message}`),
            ''
          );
        }
        dispatch(removeContact(selectedRemoveContact));
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_SUCCESS,
            alertMessage: t('client_contact_delete_success').replace(
              '{contactName}',
              `${selectedRemoveContact?.name} ${selectedRemoveContact?.surname ?? ''} ${
                selectedRemoveContact?.surnameTwo ?? ''
              }`
            ),
          })
        );
        onCancelRemove();
      } catch (ex) {
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_ERROR,
            alertMessage: t('general_delete_error'),
          })
        );
        onCancelRemove();
      }
    }
  };

  return (
    <ContactsWrapper id="contact-data">
      <ContactsHeader clientId={clientId} />
      <ContactsTable clientId={clientId} fetchContacts={fetchContacts} loadingContacts={loading} />
      {openContactModalForm && (
        <ContactModalForm
          languageField={languageField}
          setLanguageField={setLanguageField}
          clientId={clientId}
          updating={updating}
          newTypeOrganization={newTypeOrganization}
          newClientName={newClientName}
          newContactPhone={newContactPhone}
        />
      )}
      <ConfirmModal
        isOpen={isOpenDelete}
        toggle={onCancelRemove}
        confirmMessage={t('client_contact_delete_confirmation').replace(
          '{contactName}',
          `${selectedRemoveContact?.name} ${selectedRemoveContact?.surname ?? ''} ${
            selectedRemoveContact?.surnameTwo ?? ''
          }`
        )}
        cancelText={t('general_button_no')}
        okText={t('general_button_yes')}
        onCancel={onCancelRemove}
        onOk={onOkRemove}
      />
      {loading && <MainSpinner />}
    </ContactsWrapper>
  );
};

Contacts.defaultProps = {
  clientId: undefined,
  newTypeOrganization: '',
  newClientName: '',
  newContactPhone: '',
  languageField: '',
  setLanguageField: () => {},
};

Contacts.propTypes = {
  clientId: PropTypes.string,
  newTypeOrganization: PropTypes.string,
  newClientName: PropTypes.string,
  newContactPhone: PropTypes.string,
  updating: PropTypes.bool.isRequired,
  languageField: PropTypes.string,
  setLanguageField: PropTypes.func,
};

export default Contacts;
